import { useEffect, useRef, useState } from 'react';
import type { ConversationSummary, MessageWithSender, Profile } from '../types';
import { getSupabase } from '../lib/supabase';
import { uploadMessageAttachment } from '../lib/storage';
import { getConversationTitle } from '../lib/conversations';
import { useMessages } from '../hooks/useMessages';
import { useRealtimeCall } from '../hooks/useRealtimeCall';
import { useTyping } from '../hooks/useTyping';
import type { BrowserNotificationState } from '../hooks/useNotificationPermission';
import { showBrowserNotification } from '../hooks/useNotificationPermission';
import CallDock from './CallDock';
import Composer from './Composer';
import EmptyState from './EmptyState';
import MessageBubble from './MessageBubble';
import TopBar from './TopBar';

export default function ChatWindow({
  conversation,
  currentProfile,
  onlineIds,
  onBackToInbox,
  onConversationChanged,
  onOpenSettings,
  onOpenFriends,
  notificationPermission,
  onRequestNotifications,
  onProfileClick,
}: {
  conversation: ConversationSummary | null;
  currentProfile: Profile;
  onlineIds: Set<string>;
  onBackToInbox: () => void;
  onConversationChanged: () => void;
  onOpenSettings: () => void;
  onOpenFriends: () => void;
  notificationPermission: BrowserNotificationState;
  onRequestNotifications: () => void;
  onProfileClick: (profile: Profile) => void;
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const lastNotifiedMessageRef = useRef<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [replyTarget, setReplyTarget] = useState<MessageWithSender | null>(null);
  const { messages, loading, error } = useMessages(conversation?.id ?? null);
  const { typingUsers, sendTyping } = useTyping(conversation?.id ?? null, currentProfile);
  const call = useRealtimeCall(conversation?.id ?? null, currentProfile);
  const title = conversation ? getConversationTitle(conversation, currentProfile.id) : '';

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length, conversation?.id]);

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage) return;

    if (!lastNotifiedMessageRef.current) {
      lastNotifiedMessageRef.current = lastMessage.id;
      return;
    }

    if (lastMessage.id === lastNotifiedMessageRef.current) return;
    lastNotifiedMessageRef.current = lastMessage.id;

    if (lastMessage.sender_id !== currentProfile.id && document.hidden) {
      showBrowserNotification(lastMessage.sender?.display_name || title, lastMessage.content || 'Sent an attachment');
    }
  }, [currentProfile.id, messages, title]);

  useEffect(() => {
    if (!conversation || messages.length === 0) return;

    const client = getSupabase();
    client
      .from('conversation_members')
      .update({ last_read_at: new Date().toISOString() })
      .eq('conversation_id', conversation.id)
      .eq('user_id', currentProfile.id)
      .then();
  }, [conversation, currentProfile.id, messages.length]);

  async function handleSend(content: string, file: File | null) {
    if (!conversation) return;

    const client = getSupabase();
    setSendError(null);

    try {
      const attachment = file
        ? await uploadMessageAttachment(conversation.id, currentProfile.id, file)
        : {
            kind: 'text' as const,
            attachment_path: null,
            attachment_name: null,
            attachment_type: null,
            attachment_size: null,
          };

      const { error: insertError } = await client.from('messages').insert({
        conversation_id: conversation.id,
        sender_id: currentProfile.id,
        content: replyTarget
          ? `[Reply to ${replyTarget.sender?.display_name || 'message'}] ${content || 'Attachment'}`
          : content || null,
        ...attachment,
      });

      if (insertError) throw insertError;
      setReplyTarget(null);
      onConversationChanged();
    } catch (messageError) {
      setSendError(messageError instanceof Error ? messageError.message : 'Could not send message.');
      throw messageError;
    }
  }

  async function handleEdit(messageId: string, content: string) {
    const client = getSupabase();
    const { error: editError } = await client
      .from('messages')
      .update({ content, edited_at: new Date().toISOString() })
      .eq('id', messageId);

    if (editError) throw editError;
  }

  async function handleDelete(messageId: string) {
    const client = getSupabase();
    const { error: deleteError } = await client
      .from('messages')
      .update({
        content: null,
        attachment_path: null,
        attachment_name: null,
        attachment_type: null,
        attachment_size: null,
        deleted_at: new Date().toISOString(),
      })
      .eq('id', messageId);

    if (deleteError) throw deleteError;
  }

  if (!conversation) {
    return (
      <main className="hidden h-screen min-w-0 flex-1 bg-[var(--relay-center)] md:flex">
        <EmptyState
          title="Pick a conversation"
          copy="Choose a direct message, join a community channel, or create a group chat to begin."
        />
      </main>
    );
  }

  const activeConversation = conversation;
  const onlineCount = activeConversation.members.filter((member) => onlineIds.has(member.user_id)).length;

  function getReadReceipt(message: MessageWithSender) {
    if (message.sender_id !== currentProfile.id || message.deleted_at) return undefined;

    const readers = activeConversation.members.filter(
      (member) =>
        member.user_id !== currentProfile.id &&
        member.last_read_at &&
        new Date(member.last_read_at).getTime() >= new Date(message.created_at).getTime(),
    );

    if (readers.length === 0) return 'Sent';
    return `Seen by ${readers.length}`;
  }

  return (
    <main className="flex h-screen min-w-0 flex-1 flex-col bg-[var(--relay-center)]">
      <TopBar
        title={title}
        subtitle={`${onlineCount} online / ${activeConversation.members.length} members`}
        onBackToInbox={onBackToInbox}
        onOpenFriends={onOpenFriends}
        onOpenSettings={onOpenSettings}
        onRequestNotifications={onRequestNotifications}
        notificationPermission={notificationPermission}
        onStartVoice={() => void call.startCall('voice')}
        onStartVideo={() => void call.startCall('video')}
        onStartScreenShare={() => void call.startScreenShare()}
      />

      <CallDock call={call} conversationTitle={title} currentProfile={currentProfile} />

      <div ref={scrollRef} className="relay-scrollbar min-h-0 flex-1 overflow-y-auto py-3">
        {loading && <p className="px-4 py-3 text-sm text-slate-500">Loading messages...</p>}
        {error && <p className="px-4 py-3 text-sm text-relay-red">{error}</p>}
        {!loading && !error && messages.length === 0 && (
          <EmptyState title={`Start ${title}`} copy="Send a message, upload media, or record a voice note." />
        )}
        <div className="relative">
          {messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              currentProfile={currentProfile}
              isAdmin={currentProfile.role === 'admin'}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onReply={setReplyTarget}
              onProfileClick={onProfileClick}
              readReceipt={getReadReceipt(message)}
            />
          ))}
        </div>
      </div>

      <div className="min-h-6 px-4 pb-1 text-xs text-relay-cyan">
        {typingUsers.length > 0 && `${typingUsers.map((user) => user.displayName).join(', ')} typing...`}
        {sendError && <span className="text-relay-red">{sendError}</span>}
      </div>
      <Composer
        onSend={handleSend}
        onTyping={sendTyping}
        replyPreview={replyTarget ? `${replyTarget.sender?.display_name || 'Unknown'}: ${replyTarget.content || 'attachment'}` : null}
        onCancelReply={() => setReplyTarget(null)}
      />
    </main>
  );
}
